import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SeoHead from '@/components/base/SeoHead';
import JsonLd from '@/components/base/JsonLd';
import { categories } from '@/mocks/categories';
import { getComicsByCategory } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const comics = slug ? getComicsByCategory(slug) : [];

  if (!category) {
    return (
      <>
        <SeoHead
          title="分类未找到 - 韩漫网"
          description="未找到该漫画分类。请浏览其他分类获取精彩韩漫内容。"
          keywords="韩漫,漫画分类"
          canonicalUrl="https://www.hanmanwang.com/category/not-found"
        />
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-24 pb-16 px-4 md:px-6 max-w-7xl mx-auto text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">返回首页</Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const pageTitle = `${category.name}韩漫_免费${category.name}韩漫在线阅读_韩漫网`;
  const pageDesc = `韩漫网${category.name}分类——${category.description}。免费观看下拉式${category.name}韩漫，资源全面更新及时，无需下载即可畅读。`;
  const pageKeywords = `${category.name}韩漫,${category.name}漫画,韩漫网,免费韩漫,${category.name}`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.hanmanwang.com' },
      { '@type': 'ListItem', position: 2, name: `${category.name}韩漫`, item: `https://www.hanmanwang.com/category/${slug}` },
    ],
  };

  return (
    <>
      <SeoHead
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={`https://www.hanmanwang.com/category/${slug}`}
      />
      <JsonLd data={breadcrumbSchema} />

      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-16 px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: `${category.name}韩漫` }]} />

          <div className="mt-6 mb-10">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
              {category.name}韩漫
            </h1>
            <p className="text-foreground-500 mt-3 text-base md:text-lg">{category.description}</p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-10">
            <Link
              to="/category/romance"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                slug === 'romance' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              恋爱
            </Link>
            <Link
              to="/category/fantasy"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                slug === 'fantasy' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              奇幻
            </Link>
            <Link
              to="/category/action"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                slug === 'action' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              动作
            </Link>
            <Link
              to="/category/thriller"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                slug === 'thriller' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              悬疑
            </Link>
            <Link
              to="/category/comedy"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                slug === 'comedy' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              搞笑
            </Link>
            <Link
              to="/category/drama"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                slug === 'drama' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              剧情
            </Link>
            <Link
              to="/category/horror"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                slug === 'horror' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              恐怖
            </Link>
            <Link
              to="/category/slice-of-life"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                slug === 'slice-of-life' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              日常
            </Link>
          </div>

          {comics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {comics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-book-open-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画，请浏览其他分类</p>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}